import { supabase } from '../client';
import { ClassType } from '../types';
import { addHours, compareAsc, parse, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

const ARMENIA_TIMEZONE = 'Asia/Yerevan';

// Helper function to check if a class is past (more than 3 hours after start time)
export const isClassPast = (classItem: ClassType): boolean => {
  try {
    if (!classItem.date || !classItem.time) return false;
    
    const dateStr = classItem.date;
    const timeStr = classItem.time;
    
    // Parse the date and time
    const datePart = parseISO(dateStr);
    
    // Parse the time (format like "7:00 PM" or "2:00 PM")
    let hours = 0;
    let minutes = 0;
    const timeMatch = timeStr.match(/(\d+):(\d+)\s*(AM|PM)?/i);
    
    if (timeMatch) {
      hours = parseInt(timeMatch[1], 10);
      minutes = parseInt(timeMatch[2], 10);
      
      if (timeMatch[3]?.toUpperCase() === 'PM' && hours < 12) {
        hours += 12;
      }
      if (timeMatch[3]?.toUpperCase() === 'AM' && hours === 12) {
        hours = 0;
      }
    }
    
    const classDate = new Date(datePart);
    classDate.setHours(hours, minutes, 0, 0);
    
    // Convert to Armenia time
    const classDateAMT = toZonedTime(classDate, ARMENIA_TIMEZONE);
    
    // Get current time in Armenia
    const nowAMT = toZonedTime(new Date(), ARMENIA_TIMEZONE);
    
    // Add 3 hours to the class time
    const classEndTime = addHours(classDateAMT, 3);
    
    // Check if current time is after class end time (class is past)
    return compareAsc(nowAMT, classEndTime) > 0;
  } catch (error) {
    console.error('Error checking if class is past:', error);
    return false;
  }
};

// Fetch all available classes (for public pages - only upcoming classes)
export const fetchClasses = async (): Promise<ClassType[]> => {
  try {
    const { data, error } = await supabase
      .from('classes')
      .select('*')
      .order('date', { ascending: true });
    
    if (error) {
      console.error('Error fetching classes:', error);
      throw new Error(`Failed to fetch classes: ${error.message}`);
    }
    
    const classes = (data || []).map(item => ({
      id: item.id,
      title: item.title,
      day: item.day,
      date: item.date || '',
      time: item.time,
      duration: item.duration,
      capacity: item.capacity,
      availableSpots: item.availablespots,
      image: item.image,
      price: item.price || 3500 // Include price, default to 3500 if not set
    }));
    
    // Filter out past classes for public display
    const upcomingClasses = classes.filter(classItem => !isClassPast(classItem));
    
    return upcomingClasses;
  } catch (error) {
    console.error('Error in fetchClasses:', error);
    throw error;
  }
};

// Fetch all classes including past ones (for admin pages)
export const fetchAllClasses = async (): Promise<ClassType[]> => {
  try {
    const { data, error } = await supabase
      .from('classes')
      .select('*')
      .order('date', { ascending: true });
    
    if (error) {
      console.error('Error fetching classes:', error);
      throw new Error(`Failed to fetch classes: ${error.message}`);
    }
    
    return (data || []).map(item => ({
      id: item.id,
      title: item.title,
      day: item.day,
      date: item.date || '',
      time: item.time,
      duration: item.duration,
      capacity: item.capacity,
      availableSpots: item.availablespots,
      image: item.image,
      price: item.price || 3500 // Include price, default to 3500 if not set
    }));
  } catch (error) {
    console.error('Error in fetchAllClasses:', error);
    throw error;
  }
};

// Fetch a specific class by ID
export const fetchClassById = async (id: string): Promise<ClassType | null> => {
  try {
    const { data, error } = await supabase
      .from('classes')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) {
      console.error('Error fetching class:', error);
      throw new Error(`Failed to fetch class: ${error.message}`);
    }
    
    if (!data) return null;
    
    return {
      id: data.id,
      title: data.title,
      day: data.day,
      date: data.date || '',
      time: data.time,
      duration: data.duration,
      capacity: data.capacity,
      availableSpots: data.availablespots,
      image: data.image,
      price: data.price || 3500 // Include price, default to 3500 if not set
    };
  } catch (error) {
    console.error('Error in fetchClassById:', error);
    throw error;
  }
};

export const createClass = async (classData: Omit<ClassType, 'id'>): Promise<{ success: boolean; error?: string; id?: string }> => {
  try {
    console.log('Creating class with data:', classData);
    
    const dbClassData = {
      title: classData.title,
      day: classData.day,
      date: classData.date || '',
      time: classData.time,
      duration: classData.duration,
      capacity: classData.capacity,
      availablespots: classData.availableSpots,
      image: classData.image,
      price: classData.price || 3500  // Make sure price is included
    };
    
    const { data, error } = await supabase
      .from('classes')
      .insert(dbClassData)
      .select('id')
      .single();
    
    if (error) {
      console.error('Database error creating class:', error);
      throw new Error(error.message);
    }
    
    return { success: true, id: data?.id };
  } catch (error: any) {
    console.error('Error creating class:', error);
    return { success: false, error: error.message || 'An unexpected error occurred while creating class' };
  }
};

export const updateClass = async (id: string, classData: Partial<ClassType>): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('Updating class with data:', classData);
    
    const dbClassData: any = { ...classData };
    if ('availableSpots' in classData) {
      dbClassData.availablespots = classData.availableSpots;
      delete dbClassData.availableSpots;
    }
    
    const { error } = await supabase
      .from('classes')
      .update(dbClassData)
      .eq('id', id);
    
    if (error) {
      console.error('Database error updating class:', error);
      throw new Error(error.message);
    }
    
    return { success: true };
  } catch (error: any) {
    console.error('Error updating class:', error);
    return { success: false, error: error.message || 'An unexpected error occurred while updating class' };
  }
};

export const deleteClass = async (id: string): Promise<{ success: boolean; error?: string }> => {
  try {
    const { data: bookings, error: checkError } = await supabase
      .from('bookings')
      .select('id')
      .eq('class_id', id);
    
    if (checkError) {
      throw new Error('Error checking for existing bookings');
    }
    
    if (bookings && bookings.length > 0) {
      return { success: false, error: 'Cannot delete a class with active bookings' };
    }
    
    const { error } = await supabase
      .from('classes')
      .delete()
      .eq('id', id);
    
    if (error) {
      throw new Error(error.message);
    }
    
    return { success: true };
  } catch (error: any) {
    console.error('Error deleting class:', error);
    return { success: false, error: error.message || 'An unexpected error occurred while deleting class' };
  }
};
